import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { CategoryGroupFormUtils, FormData, FormErrors } from '../../utils/shopCategoryGroupCommon';
import { createCategoryGroup } from '../../utils/shopCategoryGroup';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { 
  Save,
  ArrowLeft,
  RefreshCw,
  Info,
  Tag
} from 'lucide-react';


const ShopCategoryGroupCreate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('shop-category-group-list');
  
  // Form state
  const [formData, setFormData] = useState<FormData>(CategoryGroupFormUtils.getDefaultFormData());

  // Form errors
  const [errors, setErrors] = useState<FormErrors>({});

  // API state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // Check if screen is mobile size
  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }

      try {
        setIsLoadingBrands(true);
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          console.log('프로젝트 코드가 없습니다.');
          return;
        }

        console.log('평탄화된 브랜드 목록 API 호출:', { projectCode });
        const response = await getBrandListFlat(projectCode);
        console.log('평탄화된 브랜드 목록 API 응답:', response);

        if (response && response.brands) {
          setBrands(response.brands);
          console.log('설정된 브랜드 목록:', response.brands);
        } else {
          console.warn('응답에 brands 데이터가 없습니다:', response);
          setBrands([]);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    CategoryGroupFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = CategoryGroupFormUtils.validateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form
  const handleReset = () => {
    setFormData(CategoryGroupFormUtils.getDefaultFormData());
    setErrors({});
    setSubmitError(null);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    await CategoryGroupFormUtils.handleCreateSubmit(
      e,
      formData,
      validateForm,
      setIsSubmitting,
      setSubmitError,
      getProjectCode,
      createCategoryGroup,
      navigate
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      {projectDetail && (
        <ProjectSidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          projectDetail={projectDetail}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        {projectDetail && (
          <Topbar
            title="카테고리/그룹 생성"
            subtitle={projectDetail.name}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isProfileDropdownOpen={isProfileDropdownOpen}
            setIsProfileDropdownOpen={setIsProfileDropdownOpen}
            showMobileLogo={true}
            projectDetail={projectDetail}
          />
        )}

        {/* Create Category Group Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/shops/category-groups')}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                카테고리/그룹 목록으로
              </button>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">오류가 발생했습니다</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{submitError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Info className="w-5 h-5 mr-2" />
                  기본 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Brand Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        const nbsp = '\u00A0';
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        const displayName = `${indent}${prefix}${brand.name}`;
                        
                        return (
                          <option key={brand.code} value={brand.code}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.brandCode}</p>
                    )}
                  </div>

                  {/* Group Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      코드 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      maxLength={50}
                      title="영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 입력 가능"
                      className={`w-full px-3 py-2 border ${errors.code ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="영문, 숫자, _, - 만 입력"
                    />
                    {errors.code && (
                      <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.status ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                      <option value="ARCHIVED">보관됨</option>
                    </select>
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                    )}
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.type ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">타입을 선택하세요</option>
                      <option value="CATALOG">카탈로그</option>
                      <option value="MODULE">화면 모듈</option>
                      <option value="MOBILE_ORDER">모바일 오더 카테고리</option>
                      {/* <option value="WEB_ORDER">웹 오더 카테고리</option>
                      <option value="KIOSK">키오스크 카테고리</option> */}
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                    )}
                  </div>

                  {/* Title */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      명칭 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      maxLength={50}
                      className={`w-full px-3 py-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="명칭을 입력하세요"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  {/* Subtitle */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      부제
                    </label>
                    <input
                      type="text"
                      name="subtitle"
                      value={formData.subtitle || ''}
                      onChange={handleInputChange}
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="부제를 입력하세요"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      설명
                    </label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="설명을 입력하세요"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ShopCategoryGroupCreate;